import { Inject, Injectable, InjectionToken } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { combineLatest, Observable, of, from, asapScheduler, Subject } from 'rxjs';
import {
  catchError,
  filter,
  map,
  observeOn,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

// Move is available to every case role (a plain technician is further scoped to
// their own cases below); copy is limited to admin + analyst manager; remove
// excludes plain analysts.
const CASE_MOVE_ROLES = ['admin', 'technicianmanager', 'analystmanager', 'analyst', 'technician'];
const CASE_COPY_ROLES = ['admin', 'analystmanager'];
const CASE_REMOVE_ROLES = ['admin', 'technicianmanager', 'analystmanager', 'technician'];
// The analysis-state columns (state + analyses) are only shown to analysis roles.
const CASE_ANALYSIS_VIEW_ROLES = ['admin', 'analyst', 'analystmanager'];
import { concatLatestFrom } from '@ngrx/operators';
import { WorkspaceCasesEffects } from '../../../stores/workspace-cases.effects';
import { CaseColumn } from '../../case/list/case-list.component';
import {
  WorkspaceCaseData,
  WorkspaceCaseUserData,
  WorkspaceCaseState,
  WorkspacePaginationState,
  DEFAULT_CASE_SORT_CRITERIA,
} from '../../../stores/workspace-cases.state';
import { WorkspaceDetailEffects } from '../../../stores/workspace-detail.effects';
import { AuthService, FeaturesService } from '@telespot/web-core';
import { RolesStore } from '@telespot/authentication/ui';
import { type IGetWorkspaceEncryptionStateQuery } from '@telespot/management/use-cases';
import { Router } from '@angular/router';
import { CaseSortCriteria } from '@telespot/domain';

export const GET_WORKSPACE_ENCRYPTION_STATE_QUERY = new InjectionToken<IGetWorkspaceEncryptionStateQuery>(
  'GET_WORKSPACE_ENCRYPTION_STATE_QUERY'
);

const WORKSPACE_CASE_COLUMNS: CaseColumn[] = [
  CaseColumn.STATE,
  CaseColumn.NAME,
  CaseColumn.CREATED,
  CaseColumn.ANALYSES,
  CaseColumn.MIGRATE,
  CaseColumn.COPY,
  CaseColumn.REMOVE,
];

/** Row model for the workspace case table; avoids importing CaseData from @acquisition/ui. */
export interface WorkspaceCaseListItem {
  id: string;
  name: string;
  state?: WorkspaceCaseState;
  createdAt: Date;
  createdBy?: WorkspaceCaseUserData;
  analysing?: WorkspaceCaseUserData[];
  analyzed?: WorkspaceCaseUserData[];
}

@Injectable()
export class WorkspaceCasesFacade extends ComponentStore<Record<string, never>> {
  private readonly currentUserId: string | undefined;

  private readonly permissionDeniedSubject$ = new Subject<void>();
  public readonly permissionDenied$ = this.permissionDeniedSubject$.asObservable();

  /**
   * The user roles produced by RolesStore.fetchRoles, observable as a single replayed
   * emission. RolesStore's fetchRoles flips loading=false (via listRoles) BEFORE
   * setUserRoles populates userRoles in the same sync block; observeOn(asapScheduler)
   * defers one microtask so withLatestFrom sees the post-setUserRoles userRoles, not the
   * transient empty value left over from the listRoles emission.
   */
  private readonly resolvedRoles$: Observable<string[]> = this.rolesStore.loading$.pipe(
    filter((loading) => !loading),
    take(1),
    observeOn(asapScheduler),
    withLatestFrom(this.rolesStore.userRoles$),
    map(([, userRoles]) => userRoles),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  constructor(
    private readonly casesEffects: WorkspaceCasesEffects,
    private readonly detailEffects: WorkspaceDetailEffects,
    private readonly rolesStore: RolesStore,
    @Inject(GET_WORKSPACE_ENCRYPTION_STATE_QUERY)
    private readonly encryptionStateQuery: IGetWorkspaceEncryptionStateQuery,
    authService: AuthService,
    private readonly router: Router,
    private readonly featuresService: FeaturesService
  ) {
    super({});
    const currentUserId = authService.currentUser?.id;
    this.currentUserId = currentUserId;
    if (currentUserId) {
      this.casesEffects.setCurrentUserId(currentUserId);
    }
    const organizationId = authService.currentOrganizationValue?.id;
    if (organizationId) {
      this.casesEffects.setOrganizationId(organizationId);
    }

    // Only admin users can make edits; only non-admin users can decrypt
    rolesStore.fetchRoles();
    combineLatest([rolesStore.loading$, rolesStore.isAdmin$, rolesStore.userRoles$])
      .pipe(
        map(([loading, isAdmin, userRoles]) => {
          if (loading) return false;
          if (isAdmin) return false;
          return !(userRoles.length === 1 && userRoles[0] === 'technician');
        })
      )
      .subscribe((canDecrypt) => this.casesEffects.setCanDecrypt(canDecrypt));

    // Re-fetch the workspace detail counters after each successful CRUD operation
    // (delete / move / copy). Resolved through the parent DI scope: WorkspaceDetailEffects
    // is provided at the ts-workspace-detail level and the cases route renders inside its
    // <router-outlet>.
    this.refreshDetailOnCrud(this.casesEffects.caseOperationCompleted$);
  }

  private readonly refreshDetailOnCrud = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(tap(() => this.detailEffects.refreshWorkspace()))
  );

  public readonly canEdit$ = this.rolesStore.isAdmin$;
  public readonly canDecrypt$ = this.casesEffects.canDecrypt$;

  public readonly canMigrateCases$ = this.rolesStore.userRoles$.pipe(
    map((roles) => roles.some((r) => CASE_MOVE_ROLES.includes(r))),
    startWith(false)
  );
  public readonly canCopyCases$ = this.rolesStore.userRoles$.pipe(
    map((roles) => roles.some((r) => CASE_COPY_ROLES.includes(r))),
    startWith(false)
  );
  public readonly canRemoveCases$ = this.rolesStore.userRoles$.pipe(
    map((roles) => roles.some((r) => CASE_REMOVE_ROLES.includes(r))),
    startWith(false)
  );

  // A plain technician can only act (move/remove) on cases they created.
  public readonly ownCasesOnly$ = this.rolesStore.userRoles$.pipe(
    map((roles) => roles.length === 1 && roles[0] === 'technician'),
    startWith(false)
  );

  public readonly showAnalysisState$ = this.rolesStore.userRoles$.pipe(
    map(
      (roles) =>
        roles.some((r) => CASE_ANALYSIS_VIEW_ROLES.includes(r)) && this.featuresService.canUse('reviewOthersAnalyses')
    )
  );

  public get currentUserIdValue(): string | undefined {
    return this.currentUserId;
  }

  public readonly displayColumns = WORKSPACE_CASE_COLUMNS;

  public readonly caseItems$ = combineLatest([this.casesEffects.cases$, this.casesEffects.nameEncrypted$]).pipe(
    map(([cases, nameEncrypted]) =>
      cases.map(
        (item): WorkspaceCaseListItem => ({
          id: item.id,
          name: nameEncrypted && item.encrypted ? '******' : item.name,
          state: item.state,
          createdAt: item.createdAt,
          createdBy: item.createdBy,
          analysing: item.analysing,
          analyzed: item.analyzed,
        })
      )
    )
  );

  public readonly canNavigateMap$: Observable<Record<string, boolean>> = combineLatest([
    this.caseItems$,
    this.resolvedRoles$,
  ]).pipe(
    map(([items, userRoles]) => {
      const technicianOnly = userRoles.length === 1 && userRoles[0] === 'technician';
      const acc: Record<string, boolean> = {};
      for (const item of items) {
        acc[item.id] = technicianOnly ? !!item.createdBy?.id && item.createdBy.id === this.currentUserId : true;
      }
      return acc;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // ===== Workspace initialization =====

  public activateWorkspace(workspaceId: string, autoDecrypt = false): void {
    const decryptedWsId = sessionStorage.getItem('telespot:decrypted_workspace');
    if (decryptedWsId === workspaceId) {
      autoDecrypt = true;
    }

    // watch out for decryption flag changes
    this.updateDecryptionFlag(this.casesEffects.userDecrypted$);

    const saved = (window.history.state as Record<string, unknown>)?.['paginationState'] as
      | WorkspacePaginationState
      | undefined;

    if (saved && saved.workspaceId === workspaceId) {
      const previousTokens = saved.previousPageTokens.map((tokens) =>
        tokens
          ? ({
              id: tokens.id,
              name: tokens.name ?? '',
              encrypted: false,
              createdAt: new Date(tokens.createdAt),
              updatedAt: new Date(tokens.createdAt),
              createdById: '',
            } as WorkspaceCaseData)
          : undefined
      );
      this.initializeWorkspace({
        workspaceId,
        pageSize: saved.pageSize,
        previousTokens,
        sortCriteria: saved.sortCriteria,
      });
    } else {
      this.initializeWorkspace({ workspaceId });
    }

    if (autoDecrypt) {
      this.scheduleAutoDecrypt();
    }
  }

  private readonly scheduleAutoDecrypt = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() =>
        this.casesEffects.cases$.pipe(
          filter((cases) => cases.length > 0 && cases.some((c) => c.encrypted)),
          take(1),
          tap(() => this.toggleDecryption())
        )
      )
    )
  );

  private readonly initializeWorkspace = this.effect(
    (
      params$: Observable<{
        workspaceId: string;
        pageSize?: number;
        previousTokens?: (WorkspaceCaseData | undefined)[];
        sortCriteria?: CaseSortCriteria;
      }>
    ) =>
      params$.pipe(
        tap(({ pageSize, previousTokens, sortCriteria }) => {
          // Clear workspaceId first so any sort change below does not refetch the previous workspace.
          this.casesEffects.setWorkspaceId(undefined);
          this.casesEffects.setEncrypted(false);
          this.casesEffects.setNameEncrypted(false);
          this.casesEffects.setUserDecrypted(false);
          this.casesEffects.setSortCriteria(sortCriteria ?? { ...DEFAULT_CASE_SORT_CRITERIA });
          if (pageSize || previousTokens) {
            this.casesEffects.setInitialPagination({
              pageSize,
              previousTokens,
            });
          }
        }),
        switchMap(({ workspaceId }) =>
          from(this.encryptionStateQuery.execute(workspaceId)).pipe(
            tap(({ encrypted, nameEncrypted }) => {
              this.casesEffects.setEncrypted(encrypted);
              this.casesEffects.setNameEncrypted(nameEncrypted);
              this.casesEffects.setWorkspaceId(workspaceId);
            }),
            catchError(() => {
              this.casesEffects.setEncrypted(false);
              this.casesEffects.setNameEncrypted(false);
              this.casesEffects.setWorkspaceId(workspaceId);
              return of(undefined);
            })
          )
        )
      )
  );

  // ===== Delegated store selectors =====

  get loading$() {
    return this.casesEffects.loading$;
  }
  get searchTerm$() {
    return this.casesEffects.searchTerm$;
  }
  get previousTokens$() {
    return this.casesEffects.previousTokens$;
  }
  get nextToken$() {
    return this.casesEffects.nextToken$;
  }
  get totalCaseCount$() {
    return this.casesEffects.totalCaseCount$;
  }
  get pageSize$() {
    return this.casesEffects.pageSize$;
  }
  get counting$() {
    return this.casesEffects.counting$;
  }
  get noItems$() {
    return this.casesEffects.noItems$;
  }
  get encrypted$() {
    return this.casesEffects.encrypted$;
  }
  get nameEncrypted$() {
    return this.casesEffects.nameEncrypted$;
  }
  get decrypted$() {
    return this.casesEffects.decrypted$;
  }
  get decryptingCases$() {
    return this.casesEffects.decryptingCases$;
  }
  get movingCase$() {
    return this.casesEffects.movingCase$;
  }
  get copyingCase$() {
    return this.casesEffects.copyingCase$;
  }
  get deleting$() {
    return this.casesEffects.deleting$;
  }
  get operationError$() {
    return this.casesEffects.operationError$;
  }
  get error$() {
    return this.casesEffects.error$;
  }
  get workspaceId$() {
    return this.casesEffects.workspaceId$;
  }
  get sortCriteria$() {
    return this.casesEffects.sortCriteria$;
  }

  // ===== Delegated effects / updaters =====

  get setSearchTerm() {
    return this.casesEffects.setSearchTerm;
  }
  get loadMoreItems() {
    return this.casesEffects.loadMoreItems;
  }
  get changePageSize() {
    return this.casesEffects.changePageSize;
  }
  get deleteItem() {
    return this.casesEffects.deleteItem;
  }
  get setSortCriteria() {
    return this.casesEffects.setSortCriteria;
  }

  public readonly toggleDecryption = this.casesEffects.toggleDecryption;

  private readonly updateDecryptionFlag = this.effect((decrypted$: Observable<boolean>) =>
    decrypted$.pipe(
      concatLatestFrom(() => this.casesEffects.workspaceId$),
      tap(([decrypted, workspaceId]) => {
        if (decrypted && workspaceId) {
          sessionStorage.setItem('telespot:decrypted_workspace', workspaceId);
        } else if (workspaceId) {
          sessionStorage.removeItem('telespot:decrypted_workspace');
        }
      })
    )
  );

  get moveCase() {
    return this.casesEffects.moveCase;
  }
  get copyCase() {
    return this.casesEffects.copyCase;
  }

  // ===== Navigation =====

  public navigateToCase(item: WorkspaceCaseListItem): void {
    combineLatest([this.resolvedRoles$, this.previousTokens$, this.pageSize$, this.workspaceId$, this.sortCriteria$])
      .pipe(take(1))
      .subscribe(([userRoles, previousTokens, pageSize, workspaceId, sortCriteria]) => {
        const technicianOnly = userRoles.length === 1 && userRoles[0] === 'technician';
        const allowed = technicianOnly ? !!item.createdBy?.id && item.createdBy.id === this.currentUserId : true;

        if (!allowed) {
          this.permissionDeniedSubject$.next();
          return;
        }

        const navigation: WorkspacePaginationState = {
          workspaceId: workspaceId,
          previousPageTokens: (previousTokens ?? []).map((token) =>
            token ? { id: token.id, name: token.name, createdAt: token.createdAt.toISOString() } : undefined
          ),
          pageSize,
          sortCriteria: Object.keys(sortCriteria).length > 0 ? sortCriteria : undefined,
        };

        // Record the pagination state in the browser history
        window.history.replaceState({ ...window.history.state, paginationState: navigation }, '');

        const routeComponentes = ['/analyze', 'cases', item.id];
        this.router.navigate(routeComponentes, {
          state: { paginationState: navigation },
        });
      });
  }
}
